import { useCreateProject } from "@/stores/database";
import { logger } from "@/utils/debug";
import { Alert, Button, Flex, Modal, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useState } from "react";
import { useNavigate } from "react-router";

export function WorkspaceModalProject({
  onClose,
  title = "Create New Project",
}: {
  onClose: () => void;
  title?: string;
}) {
  const createProject = useCreateProject();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const form = useForm({
    initialValues: {
      title: "",
    },
    validate: {
      title: (value) =>
        value.trim().length === 0 ? "Project name is required" : null,
    },
  });

  const handleSubmit = async (values: { title: string }) => {
    setLoading(true);
    setError(null);

    // Add timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      setLoading(false);
      setError(
        "Operation timed out. Please check your browser console for errors."
      );
    }, 15000); // 15 second timeout

    try {
      const result = await createProject({
        title: values.title.trim(),
        description: "",
      });

      clearTimeout(timeoutId);

      if (result.error) {
        const errorMessage =
          result.error instanceof Error
            ? result.error.message
            : typeof result.error === "string"
            ? result.error
            : "Failed to create project";
        logger.error("[Modal] Failed to create project:", result.error);
        setError(errorMessage);
        setLoading(false);
      } else if (result.data?.id) {
        const projectId = result.data.id;
        form.reset();
        // Set loading to false first
        setLoading(false);
        // Close modal immediately
        onClose();
        // Navigate immediately
        navigate(`/build/${projectId}`);
      } else {
        logger.error(
          "[Modal] No data returned from createProject, result:",
          result
        );
        setError("Failed to create project: No data returned");
        setLoading(false);
      }
    } catch (err) {
      clearTimeout(timeoutId);
      const errorMessage =
        err instanceof Error
          ? err.message
          : "An unexpected error occurred while creating the project";
      logger.error("[Modal] Exception creating project:", err);
      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <Modal opened={true} onClose={onClose} title={title} centered size="sm">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Flex direction="column" gap="md">
          <TextInput
            placeholder="Enter project name"
            {...form.getInputProps("title")}
            autoFocus
            disabled={loading}
          />
          {error && (
            <Alert color="red" title="Error">
              <Text size="sm">{error}</Text>
            </Alert>
          )}
          <Flex justify="flex-end" gap="sm">
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" loading={loading}>
              Create Project
            </Button>
          </Flex>
        </Flex>
      </form>
    </Modal>
  );
}
